import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { compact } from 'lodash-es';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { isNativeApp } from '../../utils/nativeApp';

function buildTargetsString(targets, t, isRtl) {
  const numNoShoots = targets.noshoots === 'single' ? 1
    : targets.noshoots === 'several' ? 50
      : (targets['paper-no-shoots'] || 0) + (targets['metal-no-shoots'] || 0);

  const parts = compact([
    targets.paper ? t('stages.ipscTarget', { count: targets.paper }) : null,
    targets.minipaper ? t('stages.ipscMiniTarget', { count: targets.minipaper }) : null,
    targets.poppers ? t('stages.popper', { count: targets.poppers }) : null,
    targets.minipoppers ? t('stages.miniPopper', { count: targets.minipoppers }) : null,
    targets.plates ? t('stages.plate', { count: targets.plates }) : null,
    targets['pcc-ten-points-poppers'] ? t('stages.tenPointPopper', { count: targets['pcc-ten-points-poppers'] }) : null,
    targets['pcc-frangible'] ? t('stages.frangible', { count: targets['pcc-frangible'] }) : null,
    targets['pcc-ten-points-frangible'] ? t('stages.tenPointFrangible', { count: targets['pcc-ten-points-frangible'] }) : null,
    numNoShoots < 50 && targets['paper-no-shoots'] ? t('stages.paperNoShoot', { count: targets['paper-no-shoots'] }) : null,
    numNoShoots < 50 && targets['metal-no-shoots'] ? t('stages.metalNoShoot', { count: targets['metal-no-shoots'] }) : null,
    numNoShoots >= 50 ? t('stages.noShootsSeveral') : null,
  ]);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const last = parts.pop();
  const m = isRtl ? '\u200F' : '';
  return parts.join(`${m}, ${m}`) + `${m}${t('common.lastSeparator')}${m}` + last;
}

export default function Stages() {
  const { t, i18n } = useTranslation();
  const { match } = useMatch();
  const [stages, setStages] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    if (match.stages?.length > 0) {
      api.getStages(match.stages.join(',')).then((res) => setStages(res.data));
    } else {
      setStages([]);
    }
  }, [match?.id]);

  if (!stages) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const handlePrint = () => window.print();

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }} className="noprint">
        <IconButton onClick={handlePrint}>
          <PrintIcon />
        </IconButton>
      </Box>

      {/* Stage Grid */}
      <Grid container spacing={2} className="noprint">
        {stages.map((stage, stageIdx) => {
          const { targets, rounds, inactive } = stage;
          const targetsStr = buildTargetsString(targets, t, i18n.dir() === 'rtl');
          const tenPointsExtra = ((targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0)) * 5;

          return (
            <Grid key={stage.id || stageIdx} size={{ xs: 6, md: 4, lg: 3 }}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 0.5, minHeight: 104 }}>
                  <Typography variant="h6">
                    {t('stages.stage')} {stageIdx + 1}
                  </Typography>
                  {inactive && (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                      {t('stages.inactive')}
                    </Typography>
                  )}
                  {!inactive && (
                    <>
                      <Typography variant="body2">
                        <b>{t('stages.targets')}</b>: {targetsStr}
                      </Typography>
                      <Typography variant="body2">
                        <b>{t('stages.rounds')}</b>: {rounds}
                      </Typography>
                    </>
                  )}
                </Box>

                <Paper
                  sx={{
                    p: 3, mt: 1, flex: 1, cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                    transition: 'box-shadow 0.2s',
                  }}
                  onClick={() => !inactive && setSelectedStage({ stage, index: stageIdx })}
                >
                  {stage.title && (
                    <Typography variant="subtitle1" sx={{ textAlign: 'center', mb: 1, fontWeight: 500 }}>
                      {stage.title}
                    </Typography>
                  )}
                  {stage.img && (
                    <Box sx={{ position: 'relative', paddingTop: '100%', width: '100%' }}>
                      <Box
                        component="img"
                        src={stage.img}
                        alt={`Stage ${stageIdx + 1}`}
                        sx={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  )}
                  {!inactive && !stage.img && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {t('stages.roundsAndPoints', { rounds, points: rounds * 5 + tenPointsExtra })}
                    </Typography>
                  )}
                </Paper>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Full stage descriptions */}
      <Box sx={{ mt: 8 }}>
        {stages.map((stage, stageIdx) => (
          <Box key={stage.id || stageIdx} sx={{ mb: 10, ...(stageIdx > 0 && { pageBreakBefore: 'always' }) }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('stages.stage')} {stageIdx + 1}{stage.title ? ` - ${stage.title}` : ''}
            </Typography>
            <StageDetail stage={stage} index={stageIdx} hideTitle />
          </Box>
        ))}
      </Box>

      {/* Stage Detail Dialog */}
      <Dialog
        open={!!selectedStage}
        onClose={() => setSelectedStage(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedStage && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('stages.stage')} {selectedStage.index + 1}{selectedStage.stage.title ? ` - ${selectedStage.stage.title}` : ''}
              <IconButton onClick={() => setSelectedStage(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <StageDetail stage={selectedStage.stage} index={selectedStage.index} hideTitle />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export { StageDetail, buildTargetsString };

function FullscreenImage({ src, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        bgcolor: 'rgba(0,0,0,0.9)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <Box
        component="img"
        src={src}
        onClick={(e) => e.stopPropagation()}
        sx={{
          maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
          touchAction: 'pinch-zoom',
          cursor: 'default',
        }}
      />
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, color: 'white' }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}

function StageDetail({ stage, index, hideTitle, hideOpenImage }) {
  const { t, i18n } = useTranslation();
  const [fullscreenImg, setFullscreenImg] = useState(null);
  const { targets, rounds, title, startPosition, gunCondition, pccCondition, start, procedure, end, comments, bonusTargets } = stage;
  const targetsStr = buildTargetsString(targets, t, i18n.dir() === 'rtl');
  const tenPointsExtra = ((targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0)) * 5;
  const paperCount = (targets.paper || 0) + (targets.minipaper || 0);
  const steelCount = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0)
    + (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-frangible'] || 0) + (targets['pcc-ten-points-frangible'] || 0);

  let roundsNote = '';
  if (paperCount > 0 && rounds === paperCount + steelCount) {
    roundsNote = t('stages.shotPerPaper1');
  } else if (paperCount > 0 && rounds === paperCount * 3 + steelCount) {
    roundsNote = t('stages.shotPerPaper3');
  }

  return (
    <Box>
      {title && !hideTitle && (
        <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
          {title}
        </Typography>
      )}

      {stage.img && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Box
            component="img"
            src={stage.img}
            alt={`Stage ${index + 1}`}
            sx={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
          />
          {!hideOpenImage && (
            <Box>
              {isNativeApp() ? (
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  sx={{ textTransform: 'none', '& .MuiButton-startIcon': { marginInlineEnd: 1, marginInlineStart: 0 } }}
                  onClick={() => setFullscreenImg(stage.img)}
                >
                  {t('stages.openImage')}
                </Button>
              ) : (
                <Button
                  component="a"
                  href={stage.img}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  sx={{ textTransform: 'none', '& .MuiButton-startIcon': { marginInlineEnd: 1, marginInlineStart: 0 } }}
                >
                  {t('stages.openImage')}
                </Button>
              )}
            </Box>
          )}
          {fullscreenImg && <FullscreenImage src={fullscreenImg} onClose={() => setFullscreenImg(null)} />}
        </Box>
      )}

      <Table dir={i18n.dir()} size="small" sx={{ '& td': { border: '1px solid rgba(0,0,0,0.2)', p: 1, textAlign: 'start' } }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: 150 }}>{t('stages.targets')}</TableCell>
            <TableCell>{targetsStr}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.rounds')}</TableCell>
            <TableCell>
              {t('stages.roundsAndPoints', { rounds, points: rounds * 5 + tenPointsExtra })}
              {roundsNote && (
                <Typography variant="caption" display="block">{roundsNote}</Typography>
              )}
            </TableCell>
          </TableRow>
          {bonusTargets && bonusTargets.length > 0 && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.bonusTargets')}</TableCell>
              <TableCell>{bonusTargets.map((s) => `T${s + 1}`).join(', ')}</TableCell>
            </TableRow>
          )}
          {startPosition && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.startPosition')}</TableCell>
              <TableCell>{startPosition}</TableCell>
            </TableRow>
          )}
          {gunCondition && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.gunCondition')}</TableCell>
              <TableCell>{gunCondition}</TableCell>
            </TableRow>
          )}
          {pccCondition && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.pccCondition')}</TableCell>
              <TableCell>{pccCondition}</TableCell>
            </TableRow>
          )}
          {start && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.start')}</TableCell>
              <TableCell>{start}</TableCell>
            </TableRow>
          )}
          {procedure && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.procedure')}</TableCell>
              <TableCell>{procedure}</TableCell>
            </TableRow>
          )}
          {end && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.end')}</TableCell>
              <TableCell>{end}</TableCell>
            </TableRow>
          )}
          {comments && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('stages.comments')}</TableCell>
              <TableCell>{comments}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
